import React, { useCallback, useMemo } from "react";
import cn from "clsx";
import {
  InputCustomChangeEventHandler,
  TextDirection,
  PicklistFieldConfig
} from "../../types";
import { get, sortAlphabeticallyByPropFn, toArray } from "../../utils/helpers";
import useAppState from "../../hooks/useAppState";
import "./InputTags.css";
import Button from "../Button";
import useT from "../../hooks/useT";
//import debug from "../../utils/debug";

export interface ReactSelectOptionObject {
  label: string;
  value: string | number | null;
  dir?: TextDirection;
}

export interface ReactSelectOptionGroupObject {
  label: string;
  options: ReactSelectOptionObject[];
  dir?: TextDirection;
}

export type ReactSelectOption =
  | ReactSelectOptionObject
  | ReactSelectOptionGroupObject;
export type ReactSelectOptions = ReactSelectOption[];

export interface InputTagsProps {
  id: string;
  name?: string;
  label?: string;
  intro?: string;
  value: string[];
  options: ReactSelectOptions;
  sortOptionsAlphabetically?: boolean;
  lang?: string;
  multiple?: boolean;
  disabled?: boolean;
  className?: string;
  onFocus?: React.ReactEventHandler;
  onBlur?: React.ReactEventHandler;
  onChange?: InputCustomChangeEventHandler;
}

const loadOptionsFromConfig = (name: string | undefined) => {
  if (!name) {
    return [];
  }
  const [appState] = useAppState();
  const picklistValues = get(
    appState.config,
    "customise.fields.picklistValues"
  );
  if (picklistValues) {
    const picklistConfig: PicklistFieldConfig = picklistValues.filter(
      (valuesConfig: PicklistFieldConfig) => valuesConfig.name === name
    )[0];
    if (picklistConfig) {
      const options: any = picklistConfig.options || [];
      picklistConfig.values &&
        picklistConfig.values.forEach(value => {
          options.push({
            value: value,
            label: value
          });
        });
      return options;
    }
  }
  return [];
};

export default function InputTags({
  id,
  name,
  label,
  intro,
  value,
  options,
  sortOptionsAlphabetically = true,
  lang,
  multiple = true,
  disabled = false,
  className,
  onChange
}: InputTagsProps) {
  const { t } = useT();
  if (!value) {
    value = [];
  }
  if (!options) {
    const configOptions = loadOptionsFromConfig(name);
    //debug("[InputTags] configOptions:", configOptions);
    options = configOptions;
  }

  const tagsOptions = useMemo(() => {
    //debug("[InputTags] tagOptions:", options);
    // No need to sort
    if (!sortOptionsAlphabetically) {
      return options;
    }

    // Let the sorting ceremony commence!
    const _options = toArray(options);

    // Sort everything else after
    return _options.sort(sortAlphabeticallyByPropFn("label"));
  }, [options]);

  const [, updateState] = React.useState();

  const clear = () => {
    value.splice(0, value.length);
    updateState({});
  };

  const handleClick = useCallback(
    (clickedTag: any) => {
      const i = value.indexOf(clickedTag.value);
      if (i > -1) {
        value.splice(i, 1);
      } else {
        value.push(clickedTag.value);
      }
      updateState({});
    },
    [onclick]
  );

  return (
    <div
      id={`input-tags-${id}`}
      className={cn(
        "input-tags",
        !!multiple && "is-multiple",
        !!value && value.length && "has-value",
        className
      )}
    >
      <div className="input-tags-header">
        <span>
          <Button
            type="button"
            shape="solid"
            label={t("general.buttons.clear.label")}
            onClick={clear}
          />
        </span>
      </div>
      {label && (
        <div className="section-header align-left m-bottom-small configurable-form-field">
          <h3 className="section-header-title">{t(label)}</h3>
        </div>
      )}
      {intro && (
        <div className="section-text align-left configurable-form-field">
          <p>{t(intro)}</p>
        </div>
      )}
      <div className="input-tags-tags-wrap">
        {tagsOptions &&
          tagsOptions.map(tag => (
            <span key={tag.value} className="tag-wrap">
              <Button
                type="button"
                shape="solid"
                theme={value.indexOf(tag.value) > -1 ? "primary" : "neutral"}
                label={tag.label}
                onClick={handleClick.bind(null, tag)}
              />
            </span>
          ))}
      </div>
    </div>
  );
}
